import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';
import type { Category } from '../../types';
import useApi from '../../hooks/api';
import { useAppDispatch, useAppSelector } from '../../hooks/store';
import { categoriesSet } from '../../store/categoriesSlice';
import './CategoryList.css';

type Categories = {
	categories: Category[]
};

export interface CategoryListProps {
    onChange: (value: Category|null) => void;
};

const CategoryList: FC<CategoryListProps> = ({ onChange }) => {
	const { t, i18n } = useTranslation();
	const lang = i18n.language === 'en' ? 'en' : 'ru';
	const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const user = useAppSelector(store => store.auth.user);
    const event = useAppSelector(store => store.event);
    const dispatch = useAppDispatch();
    const ref = useRef(null);
    const [ isOpen , setIsOpen ] = useState(false);
    const [ data, error, isLoading ] = useApi('get', '/categories', { event_id: event?.id ?? null });
    const [ current, setCurrent ] = useState<Category|null>(null);
    const [ categories, setCategories ] = useState<Category[]>([]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if(ref.current && !(ref.current as any).contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    useEffect(() => {
		if(data) {
            // Для редактирования своего профайла запомним список всех категорий
			dispatch(categoriesSet((data as Categories).categories));
            // А из отображения в фильтре уберем категории, которые никто не выбрал
            const counter = user?.role === 'bayer' ? 'sellers' : 'bayers';
            let categories = (data as Categories).categories.filter(c => c[counter] > 0);
            categories.sort((a, b) => {
                if(a[counter] > b[counter]) return -1;
                if(a[counter] < b[counter]) return  1;
                return a[name].localeCompare(b[name]);
            });
            setCategories(categories);
		}
	}, [ data, dispatch, user, name ]);

    const onClick = (category: Category|null) => {
        setCurrent(category);
        setIsOpen(false);
        onChange(category);
    };

    if(!event) return <div className='error'>{t('common.noevent')}</div>;
    if(isLoading) return <div>{t('common.loading')}</div>;
    if(error) return <div className='error'>{error.text[lang]}</div>;

    return (
        <div className='CategoryList'>
            <div onClick={() => setIsOpen(true)}>
                <span>{current ? current[name] : t('search.allcategories')}</span>
                <span><Icon icon={faChevronDown}/></span>
            </div>
            {isOpen &&
            <ul ref={ref}>
                <li onClick={() => onClick(null)} className={current ? undefined : 'current'}>
                    <span>{t('search.allcategories')}</span>
                    {current ? <span/> : <span><Icon icon={faChevronUp}/></span>}
                </li>
                {categories.map(category => (
                    <li 
                        key={category.id}
                        className={category.id === current?.id ? 'current' : undefined}
                        onClick={() => onClick(category)}
                    >
                        <span>
                            {category[name]}
                            <small>
                                {t('search.attendee_count', { count: user?.role === 'bayer' ? category.sellers : category.bayers })}
                            </small>
                        </span>
                        {category.id === current?.id ? <span><Icon icon={faCheck}/></span> : <span/>}
                    </li>
                ))}
            </ul>}
        </div>
	);
};

export default CategoryList;
